// Data hooks behind every table, KPI and chart, over /api/analytics/*. Each
// takes the current filter selection and date range; tables return rows one
// level below the selection (county -> its subcounties -> ...), so every
// table drills down as the filters narrow.

import { useQuery } from '@tanstack/react-query';
import {
  analyticsApi, AnalyticsFigures, AnalyticsFilters, IdDemoBy, idsApi, NOT_IN_NRB, registeredApi, RegFilters, RegLevel, RegPivotBy,
  RegRowApi,
} from '@services/api/analytics';

/** Registered voters with no NRB record have a county but no area below it.
 *  They stay in every county total; the area tables just don't list them
 *  as a row of their own. */
const listed = (name?: string) => name !== NOT_IN_NRB;
import { DateFilterValue } from './ui';

const STALE = 5 * 60 * 1000;

/** The API's date parameters for a date filter ('all' sends none). */
const dateParams = (d: DateFilterValue) =>
  d.preset === 'all' ? { date_from: '', date_to: '' } : { date_from: d.from, date_to: d.to };

// ---- NRB tree (IDs pages): County > Subcounty > Division > Location ------------

export interface NrbFilter { county: string; subcounty: string; division: string; location: string }
export const emptyNrb: NrbFilter = { county: '', subcounty: '', division: '', location: '' };
export const NRB_LEVELS = ['county', 'subcounty', 'division', 'location'] as const;
export type NrbLevel = typeof NRB_LEVELS[number];

/** Level names; the diaspora and prisons groups have their own tree. */
export function nrbLabel(level: NrbLevel, county: string): string {
  if (county === 'DIASPORA') return { county: 'County', subcounty: 'Country', division: 'Embassy / consulate', location: 'Polling centre' }[level];
  if (county === 'PRISONS') return { county: 'County', subcounty: 'Group', division: 'Section', location: 'Prison' }[level];
  return { county: 'County', subcounty: 'Subcounty', division: 'Division', location: 'Location' }[level];
}

/** The levels worth a dropdown: prisons have only the prison; a diaspora
 *  mission's polling centre is the mission itself. */
export function nrbVisibleLevels(county: string): NrbLevel[] {
  if (county === 'PRISONS') return ['county', 'location'];
  if (county === 'DIASPORA') return ['county', 'subcounty', 'division'];
  return [...NRB_LEVELS];
}

/** Fills the levels hidden for prisons (cmd/voterareas files every prison
 *  under PRISONS / Prisons / Prisons) and drops a diaspora "location". */
export function normalizeNrb(f: NrbFilter): NrbFilter {
  if (f.county === 'PRISONS') return { ...f, subcounty: 'Prisons', division: 'Prisons' };
  if (f.county === 'DIASPORA') return { ...f, location: '' };
  return f;
}

/** The level the tables group by: one below the deepest filter set. */
export function nrbChildLevel(f: NrbFilter): NrbLevel | '' {
  if (f.location) return '';
  if (f.division) return 'location';
  if (f.subcounty) return 'division';
  if (f.county) return 'subcounty';
  return 'county';
}

const nrbParams = (f: NrbFilter, d: DateFilterValue): AnalyticsFilters => ({ ...f, ...dateParams(d) });

/** One dropdown's choices, given the levels above it. */
export function useNrbOptions(level: NrbLevel, f: NrbFilter) {
  const i = NRB_LEVELS.indexOf(level);
  const parents: Partial<AnalyticsFilters> = {};
  NRB_LEVELS.slice(0, i).forEach((l) => { parents[l] = f[l]; });
  const ready = NRB_LEVELS.slice(0, i).every((l) => f[l]);
  const q = useQuery({
    queryKey: ['analytics', 'options', parents],
    queryFn: () => analyticsApi.options(parents),
    enabled: ready,
    staleTime: 30 * 60 * 1000,
  });
  return { values: ready ? (q.data?.values ?? []).filter(listed) : [], loading: q.isFetching };
}

export interface IdRow {
  key: string;
  name: string;
  ids: number;
  registered: number;
  notRegistered: number;
  pctRegistered: number;
}

const toIdRow = (r: AnalyticsFigures): IdRow => ({
  key: r.name || '',
  name: r.name || '',
  ids: r.total,
  registered: r.registered,
  notRegistered: r.unregistered,
  pctRegistered: r.registered_pct,
});

/** Rows one level below the filters. */
export function useIdRows(f: NrbFilter, d: DateFilterValue) {
  const params = nrbParams(f, d);
  const q = useQuery({
    queryKey: ['analytics', 'breakdown', params],
    queryFn: () => analyticsApi.breakdown(params),
    staleTime: STALE,
  });
  return { rows: (q.data?.rows ?? []).filter((r) => listed(r.name)).map(toIdRow), loading: q.isLoading, fetching: q.isFetching, error: q.error };
}

export function useIdTotals(f: NrbFilter, d: DateFilterValue) {
  const params = nrbParams(f, d);
  const q = useQuery({
    queryKey: ['analytics', 'summary', params],
    queryFn: () => analyticsApi.summary(params),
    staleTime: STALE,
  });
  const s = q.data?.summary;
  return {
    t: s ? toIdRow(s) : null,
    viaApp: s?.registered_via_app ?? 0,
    loading: q.isLoading,
    error: q.error,
  };
}

export type TrendBy = 'day' | 'month' | 'year';
export const TREND_LABELS: Record<TrendBy, string> = { day: 'Day', month: 'Month', year: 'Year' };

export function useIdTrend(f: NrbFilter, d: DateFilterValue, by: TrendBy) {
  const params = nrbParams(f, d);
  const q = useQuery({
    queryKey: ['analytics', 'trend', params, by],
    queryFn: () => analyticsApi.trend(params, by),
    staleTime: STALE,
  });
  return { points: q.data?.points ?? [], loading: q.isLoading, error: q.error };
}

// ---- IEBC tree (Registered voters): County > Constituency > Ward > Polling centre

export interface IebcFilter { county: string; constituency: string; ward: string; pollingCentre: string }
export const emptyIebc: IebcFilter = { county: '', constituency: '', ward: '', pollingCentre: '' };
export const IEBC_LEVELS = ['county', 'constituency', 'ward', 'pollingCentre'] as const;
export type IebcLevel = typeof IEBC_LEVELS[number];
export const IEBC_LABELS: Record<IebcLevel, string> = { county: 'County', constituency: 'Constituency', ward: 'Ward', pollingCentre: 'Polling centre' };
const API_LEVEL: Record<IebcLevel, RegLevel> = { county: 'county', constituency: 'constituency', ward: 'ward', pollingCentre: 'polling_centre' };

const regParams = (f: IebcFilter, d: DateFilterValue): RegFilters => ({
  county: f.county, constituency: f.constituency, ward: f.ward, polling_centre: f.pollingCentre, ...dateParams(d),
});

export function iebcChildLevel(f: IebcFilter): IebcLevel {
  if (f.ward) return 'pollingCentre';
  if (f.constituency) return 'ward';
  if (f.county) return 'constituency';
  return 'county';
}

export function useIebcOptions(level: IebcLevel, f: IebcFilter) {
  const i = IEBC_LEVELS.indexOf(level);
  const ready = IEBC_LEVELS.slice(0, i).every((l) => f[l]);
  const parents = regParams({ ...emptyIebc, ...Object.fromEntries(IEBC_LEVELS.slice(0, i).map((l) => [l, f[l]])) }, { preset: 'all', from: '', to: '' });
  const q = useQuery({
    queryKey: ['analytics', 'reg-options', level, parents],
    queryFn: () => registeredApi.options(API_LEVEL[level], parents),
    enabled: ready,
    staleTime: 30 * 60 * 1000,
  });
  return { values: ready ? q.data?.values ?? [] : [], loading: q.isFetching, first: q.data?.date_from ?? '', last: q.data?.date_to ?? '' };
}

export interface RegRow extends RegRowApi {
  key: string;
  pctFemale: number;
  pctYouth: number;
}

export function useRegRows(f: IebcFilter, d: DateFilterValue) {
  const params = regParams(f, d);
  const q = useQuery({
    queryKey: ['analytics', 'reg-rows', params],
    queryFn: () => registeredApi.rows(params),
    staleTime: STALE,
  });
  const rows: RegRow[] = (q.data?.rows ?? []).map((r) => ({
    ...r,
    key: r.name,
    pctFemale: r.male + r.female ? (r.female / (r.male + r.female)) * 100 : 0,
    pctYouth: r.age_recorded ? (r.youth / r.age_recorded) * 100 : 0,
  }));
  return { rows, loading: q.isLoading, fetching: q.isFetching, error: q.error };
}

export type PivotBy = RegPivotBy;
export const PIVOT_BY_LABELS: Record<PivotBy, string> = {
  month: 'Month', week: 'Week', day: 'Day', year: 'Year', sex: 'Sex', age: 'Age group', tribe: 'Tribe',
};
/** Sex, age and tribe come per ward, so their pivots stop at ward. */
export const DEMOGRAPHIC: PivotBy[] = ['sex', 'age', 'tribe'];
/** Up to this many days a day-by-day pivot stays readable. */
export const MAX_DAY_COLUMNS = 62;

export function useRegPivot(f: IebcFilter, d: DateFilterValue, rowsBy: IebcLevel, by: PivotBy, enabled = true) {
  const params = regParams(f, d);
  const q = useQuery({
    queryKey: ['analytics', 'reg-pivot', params, rowsBy, by],
    queryFn: () => registeredApi.pivot(params, API_LEVEL[rowsBy], by),
    staleTime: STALE,
    enabled,
  });
  return { pivot: q.data?.pivot, loading: q.isLoading, fetching: q.isFetching, error: q.error };
}

// ---- ID holders by sex / age group / tribe ---------------------------------------

export const ID_DEMO_LABELS: Record<IdDemoBy, string> = { tribe: 'Tribe', sex: 'Sex', age: 'Age group' };
export type IdMeasure = 'ids' | 'registered' | 'notRegistered';
export const ID_MEASURE_LABELS: Record<IdMeasure, string> = { ids: 'ID holders', registered: 'Registered', notRegistered: 'Not registered' };

/** ID holders under the area filters by sex / age / tribe; grouped=false gives one total row. */
export function useIdPivot(f: NrbFilter, by: IdDemoBy, grouped = true) {
  const q = useQuery({
    queryKey: ['analytics', 'id-pivot', f, by, grouped],
    queryFn: () => idsApi.pivot(f, by, grouped),
    staleTime: STALE,
  });
  const pivot = q.data?.pivot && { ...q.data.pivot, rows: q.data.pivot.rows.filter((r) => listed(r.key)) };
  return { pivot, loading: q.isLoading, fetching: q.isFetching, error: q.error };
}

export interface TribeRow { tribe: string; ids: number; registered: number; notRegistered: number; pct: number }

/** Per-tribe totals from a one-row tribe pivot. */
export function useTribeSummary(f: NrbFilter) {
  const { pivot, loading, fetching } = useIdPivot(f, 'tribe', false);
  const rows: TribeRow[] = (pivot?.columns ?? []).map((t, j) => {
    const registered = (pivot?.rows ?? []).reduce((s, r) => s + r.registered[j], 0);
    const notRegistered = (pivot?.rows ?? []).reduce((s, r) => s + r.unregistered[j], 0);
    const ids = registered + notRegistered;
    return { tribe: t, ids, registered, notRegistered, pct: ids ? (registered / ids) * 100 : 0 };
  });
  return { rows, loading, fetching };
}
